import { useQuery, useMutation, useQueryClient, QueryClient } from '@tanstack/react-query';
import { apiClient } from './api-client';
import { useSession } from '@/contexts/SessionContext';
import { useActiveMatches } from './useActiveMatches';
import type { LobbyResponse, LobbyRound, LobbySlot } from './lobby.types';

export const LOBBY_QUERY_KEY = ['squads', 'lobby'] as const;

/**
 * Swap one slot into the cached lobby
 *
 * Reveal and select both return the updated slot, so the card can change the moment the request
 * resolves rather than after a full lobby refetch. The invalidate still runs behind it so
 * anything else that moved arrives moments later
 */
const patchSlot = (queryClient: QueryClient, slot: LobbySlot) => {
  queryClient.setQueryData<LobbyResponse>(
    LOBBY_QUERY_KEY,
    current =>
      current && {
        ...current,
        rounds: current.rounds.map(round =>
          round.userRound
            ? {
                ...round,
                userRound: {
                  ...round.userRound,
                  slots: round.userRound.slots.map(s => (s.id === slot.id ? slot : s)),
                },
              }
            : round
        ),
      }
  );
};

export function useLobby() {
  const session = useSession();
  const { data: matchesData } = useActiveMatches();

  const hasLiveMatch =
    matchesData?.matches.some(m => m.status === 'live' || m.status === 'half_time') ?? false;

  return useQuery<LobbyResponse>({
    queryKey: LOBBY_QUERY_KEY,
    queryFn: async () => (await apiClient.get('/api/squads/lobby')) as unknown as LobbyResponse,
    enabled: session.status === 'ready',
    refetchOnWindowFocus: true,
    staleTime: 30_000,
    refetchInterval: hasLiveMatch ? 30_000 : 60_000,
  });
}

/**
 * useEnterRound
 *
 * POST /api/squads/rounds/:roundId/enter — first entry into a round, where the
 * backend pre-draws the entire squad server-side under the round's GPG
 * constraint. The lobby fires this automatically when the focused round is
 * enterable and the user has no entry yet; the refetch then delivers the
 * pre-drawn (still hidden) slots. A 409 means "already entered" or "closed to
 * entry" — both resolve by refetching rather than surfacing an error.
 */
export function useEnterRound() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (roundId: string) => apiClient.post(`/api/squads/rounds/${roundId}/enter`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LOBBY_QUERY_KEY });
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: LOBBY_QUERY_KEY });
    },
  });
}

/**
 * useRevealSlot
 *
 * Calls POST /api/squads/slots/:slotId/reveal after the card flip animation
 * completes, persisting the revealed state to the DB. On success the lobby
 * query is invalidated so the next fetch reflects the updated slot status.
 */
export function useRevealSlot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (slotId: string) =>
      (await apiClient.post(`/api/squads/slots/${slotId}/reveal`)) as unknown as {
        slot: LobbySlot;
      },
    onSuccess: ({ slot }) => {
      patchSlot(queryClient, slot);
      queryClient.invalidateQueries({ queryKey: LOBBY_QUERY_KEY });
    },
  });
}

/**
 * POST /api/squads/slots/:slotId/select commits one
 * candidate to the slot. Final: the backend rejects a
 * second selection with a 409, so a stale UI resolves
 * by refetching rather than surfacing with an error
 */
export function useSelectCandidate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ slotId, candidateId }: { slotId: string; candidateId: string }) =>
      (await apiClient.post(`/api/squads/slots/${slotId}/select`, { candidateId })) as unknown as {
        slot: LobbySlot;
      },
    onSuccess: ({ slot }) => {
      patchSlot(queryClient, slot);
      queryClient.invalidateQueries({ queryKey: LOBBY_QUERY_KEY });
    },
    onError: (error: { statusCode?: number }) => {
      if (error.statusCode === 409) {
        queryClient.invalidateQueries({ queryKey: LOBBY_QUERY_KEY });
      }
    },
  });
}

/**
 * Earliest `revealStart` and latest `revealEnd` for a round, derived from its
 * `revealDays` array. Returns null if the round has no reveal days configured.
 */
function roundBounds(r: LobbyRound): { start: string; end: string } | null {
  if (r.revealDays.length === 0) return null;
  let start = r.revealDays[0].revealStart;
  let end = r.revealDays[0].revealEnd;
  for (const d of r.revealDays) {
    if (d.revealStart < start) start = d.revealStart;
    if (d.revealEnd > end) end = d.revealEnd;
  }
  return { start, end };
}

/**
 * Pick the round the lobby should focus on, given the schedule:
 *   1. an active round (now falls inside [earliest revealStart, latest revealEnd))
 *   2. else the most recent past round (latest revealEnd ≤ now) — between
 *      rounds the lobby stays on the round just played (results, leaderboard)
 *      rather than jumping ahead to one that hasn't opened yet
 *   3. else the next upcoming round (earliest revealStart > now)
 *   4. else the first round in the list
 */
export function pickTodaysRound(rounds: LobbyRound[]): LobbyRound | null {
  if (rounds.length === 0) return null;
  const now = new Date().toISOString();

  const withBounds = rounds
    .map(r => ({ r, b: roundBounds(r) }))
    .filter((x): x is { r: LobbyRound; b: { start: string; end: string } } => x.b !== null);

  const active = withBounds.find(({ b }) => b.start <= now && b.end > now);
  if (active) return active.r;

  const past = withBounds
    .filter(({ b }) => b.end <= now)
    .sort((a, b) => b.b.end.localeCompare(a.b.end))[0];
  if (past) return past.r;

  const upcoming = withBounds
    .filter(({ b }) => b.start > now)
    .sort((a, b) => a.b.start.localeCompare(b.b.start))[0];
  return upcoming ? upcoming.r : rounds[0];
}
